import type { ProcedureType } from '@/types/cgt'

/**
 * Where a procedure's detail page lives, by the kind of run it is.
 *
 * One definition, because the CAPA page needs it for a run of ANY kind. A CAPA
 * is a form raised inside a transplant, a kit build or an MES order, so clicking
 * a CAPA row opens whichever page its host run lives on — the page it was
 * clicked from cannot answer that.
 *
 * Getting this wrong is not theoretical: while the redirect and the row click
 * each had their own idea of it, a new CAPA opened as an MES order, patient
 * breadcrumb and all.
 *
 * Null when the run has no page it can be opened on: patient care nests under
 * the MRN, and a run without one has no such URL. Callers must not navigate.
 */
export function procedureRunPath(
  type: ProcedureType | null | undefined,
  procedureId: string,
  patientMrn: string | null,
): string | null {
  const id = encodeURIComponent(procedureId)
  if (type === 'KitCreation') return `/kits/runs/${id}`
  if (type === 'MES') return `/mes/orders/${id}`
  // A room's clean or a machine's maintenance: no patient, opened from the
  // Cleaning / Equipment page's Pending Actions (z8nrz7ewt1).
  if (type === 'Cleaning') return `/cleaning/runs/${id}`
  if (type === 'Equipment') return `/equipment/runs/${id}`
  if (!patientMrn) return null
  return `/patients/${encodeURIComponent(patientMrn)}/procedures/${id}`
}

/**
 * The same page, opened ON one of its forms.
 *
 * An ad-hoc submission — which every CAPA is — appears in the procedure's step
 * list under the synthetic id `adhoc:<submissionId>`, and the detail page reads
 * `?step=` to select it. Without this, clicking a CAPA row opened its run on
 * whatever step happened to be actionable, and the CAPA the row named was
 * somewhere further down the sidebar.
 */
export function adHocStepQuery(submissionId: string): string {
  return `step=${encodeURIComponent(`adhoc:${submissionId}`)}`
}

/** Append a query string to a path that may already carry one. */
export function withQuery(path: string, query: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}
